import { format } from 'date-fns';
import parseISO from 'date-fns/parseISO';
import { camelcaseKeys } from 'src/utils/camelize-keys';
import { decamelizeKeys } from 'src/utils/decamelize-keys';
import {
  centsToDollars,
  removeReadOnlyFields,
} from 'src/utils/fieldNormalizers';

import { ScheduledAppointment, ScheduledMembership, Visit } from './_types';

const readOnlyFields = [
  'user_name',
  'client_id',
  'created_at',
  'user_card_on_file',
  'with_guest',
  'booked_by_name',
  'user',
  'appointment',
  'color',
  'workout_id',
  'cp_id',
  'workout_date',
  'time_zone',
  'location_id',
  'first_name',
  'last_name',
  'email',
  'phone_number',
  'has_membership',
  'head_trainer_id',
  'cancelled_by_id',
  'cancelled_by_name',
  'chaperone_visit_id',
  'chaperone_user_id',
  'service_price',
  'no_require_prepayment',
  'waitlist_entry_id',
  'check_in',
  'check_out',
  'check_in_at',
  'check_out_at',
  'booked_by_id',
  'updated_at',
  'late_fee',
  'noshow_fee',
  'links',
  'resources',
  'first_visit',
  'package',
];

const membershipReadOnlyFields = [
  'user_card_on_file',
  'client_id',
  'user_name',
  'updated_at',
  'scheduled_appointment',
  'created_at',
  'id',
];

export function normalizeVisit(visit): Visit {
  const normalized = camelcaseKeys(visit, { deep: true });
  if (normalized.lateFee) {
    normalized.lateFee.amount = centsToDollars(normalized.lateFee.amount);
  }
  if (normalized.noshowFee) {
    normalized.noshowFee.amount = centsToDollars(normalized.noshowFee.amount);
  }
  return normalized;
}

export function denormalizeVisit(visit: Partial<Visit>) {
  const decamelized = decamelizeKeys(visit, { deep: true });

  removeReadOnlyFields(decamelized, readOnlyFields);

  return decamelized;
}
export function normalizeScheduledMembership(membership): ScheduledMembership {
  const normalized = camelcaseKeys(membership, { deep: true });
  if (normalized?.startAt) {
    normalized.startAt = parseISO(normalized.startAt);
  }
  if (normalized?.endAt) {
    normalized.endAt = parseISO(normalized.endAt);
  }
  return normalized;
}

export function denormalizeScheduledMembership(
  membership: Partial<ScheduledMembership>
) {
  const decamelized = decamelizeKeys(membership, { deep: true });

  if (decamelized?.start_at) {
    (decamelized.start_at as any) = format(membership.startAt!, 'yyyy-MM-dd');
  }

  if (decamelized?.end_at) {
    (decamelized.end_at as any) = format(membership.endAt!, 'yyyy-MM-dd');
  } else {
    (decamelized.end_at as any) = null;
  }

  removeReadOnlyFields(decamelized, membershipReadOnlyFields);

  return decamelized;
}

export function normalizeScheduledAppointment(
  appointment
): ScheduledAppointment {
  const normalized = camelcaseKeys(appointment, { deep: true });
  if (normalized?.startAt) {
    normalized.startAt = parseISO(normalized.startAt);
  }
  if (normalized?.endAt) {
    normalized.endAt = parseISO(normalized.endAt);
  }
  if (normalized?.recurringStartAt) {
    normalized.recurringStartAt = parseISO(normalized.recurringStartAt);
  }
  return normalized;
}
